import * as pc from "playcanvas";
import type { FirstPersonController } from "../player/FirstPersonController";
import type { CharacterFactory } from "../player/CharacterFactory";
import type { RemotePlayerState } from "../net/GameClient";
import { RemotePlayer } from "../player/RemotePlayer";

const CT_ACCENT = new pc.Color(0.32, 0.45, 0.72); // blue
const T_ACCENT = new pc.Color(0.74, 0.42, 0.22); // orange
const SKY = new pc.Color(0.52, 0.72, 0.90);

/** Platform top (the 12×12 mid block spans y 0..1.5). */
const STAGE_Y = 1.5;
/** Centred — the mannequin is the hero of the result shot. */
const MANNEQUIN_X = 0;

/** Gameplay HUD layers hidden while the result screen is up — the overlay is
 *  transparent on the left so the mannequin shows, which would otherwise
 *  expose the crosshair/HUD. Scoped to a body class. */
const HUD_HIDE_CSS =
  "body.match-end-active #crosshair," +
  "body.match-end-active #scoreboard," +
  "body.match-end-active #hud-center," +
  "body.match-end-active #weapon-inv," +
  "body.match-end-active #kill-feed{display:none!important;}";

/**
 * MatchEndCinematic — the 3D side of the end-of-match result screen.
 *
 * Stages the LOCAL player's mannequin (team-tinted via {@link RemotePlayer})
 * playing the match-result animation — silly dance on a win, defeat slump on
 * a loss — framed by a dedicated camera, leaving the right side open for
 * {@link MatchEndOverlay}'s stats panel. Terminal — it stays up until the
 * player returns to the lobby (which tears the iframe down).
 */
export class MatchEndCinematic {
  private readonly app: pc.AppBase;
  private readonly player: FirstPersonController;
  private readonly factory: CharacterFactory;

  private active = false;
  private fighter: RemotePlayer | null = null;
  private camera: pc.Entity | null = null;
  private elapsed = 0;

  constructor(app: pc.AppBase, player: FirstPersonController, factory: CharacterFactory) {
    this.app = app;
    this.player = player;
    this.factory = factory;
    this.app.on("update", (dt: number) => this.update(dt));

    const style = document.createElement("style");
    style.textContent = HUD_HIDE_CSS;
    document.head.appendChild(style);
  }

  /** Stage the local mannequin for its final team, playing the match-result
   *  pose: silly dance on a win, defeat slump on a loss. */
  show(team: "ct" | "t", won: boolean): void {
    if (this.active) return;
    this.active = true;
    this.elapsed = 0;
    document.body.classList.add("match-end-active");

    const playerCam = this.player.getCameraEntity();
    if (playerCam.camera) playerCam.camera.enabled = false;

    const cam = new pc.Entity("match-end-camera");
    cam.addComponent("camera", { clearColor: SKY, fov: 50, nearClip: 0.05, farClip: 200 });
    this.app.root.addChild(cam);
    this.camera = cam;
    this.placeCamera(0);

    const weaponId = team === "t" ? "ak47" : "m4a1_s";
    const state: RemotePlayerState = {
      sessionId: "matchend-self",
      name: "matchend-self",
      x: MANNEQUIN_X,
      y: STAGE_Y,
      z: 0,
      yaw: 0,
      pitch: 0,
      health: 100,
      weaponId,
      lastProcessedInputSeq: 0,
      team,
    };
    this.fighter = new RemotePlayer(
      this.app,
      "matchend-self",
      state,
      team === "ct" ? CT_ACCENT : T_ACCENT,
      this.factory,
    );
    this.fighter.playResult(won);
  }

  /** Close, head-on and centred so the mannequin is big and its result
   *  animation is the hero of the shot. Slow push-in, no flashing. */
  private placeCamera(t: number): void {
    if (!this.camera) return;
    const z = -4.4 + 0.4 * t;
    const y = 2.5 - 0.12 * t;
    this.camera.setPosition(0, y, z);
    this.camera.lookAt(0, 2.15, 0);
  }

  private update(dt: number): void {
    if (!this.active) return;
    this.elapsed += dt;
    this.fighter?.update(dt);
    this.placeCamera(Math.min(1, this.elapsed / 12));
  }
}
